import { SendDocumentMail } from "@/components/mail/SendDocumentMail";
import { useEffect, useId, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PackagePlus, Printer, Trash2 } from "lucide-react";
import {
  checkSampleRequired, type SampleAttachmentDto,
  type SampleOrderType,
  type SampleStatus,
  vnDate,
} from "@hisense/shared";
import { PageActions, PageBody, PageHeader } from "@/components/ui/PageHeader";
import { PrintLanguageToggle } from "@/components/docs/PrintLanguageToggle";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { ProductPickerModal } from "@/components/samples/ProductPickerModal";
import {
  SampleOrderLinesEditor,
  sampleDraftsToInput,
  sampleLineFromDto,
  sampleLineFromProduct,
  validateSampleLines,
  type SampleLineDraft,
} from "@/components/samples/SampleOrderLinesEditor";
import {
  SampleOrderAttachments,
  type AttachmentDraft,
} from "@/components/samples/SampleOrderAttachments";
import { initialSampleHeader, sampleShippingFilled, type SampleHeaderDraft } from "@/components/samples/sampleFormLayout";
import { FormGroup, Hint, MoreBlock } from "@/components/ui/FormLayout";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useCreateSampleOrder,
  useCustomers,
  useDeleteSampleOrder,
  useProduct,
  useSampleOrder,
  useSettings,
  useSuppliers,
  useUpdateSampleOrder,
} from "@/api/hooks";
import { SamplePrintPreview } from "@/components/samples/SamplePrintPreview";
import { SampleCatalogDialog, sampleError } from "@/components/samples/SampleCatalogDialog";
import { translateStatus } from "@/domain/i18n";
import { SaveButton } from "@/components/ui/SaveButton";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { cn } from "@/lib/cn";

const toDateInput = (ms: number | null | undefined) => (ms ? vnDate(ms) : "");
const fromDateInput = (s: string) => (s ? new Date(`${s}T00:00:00Z`).getTime() : undefined);

/** The 7 literals of sampleStatusSchema — status is set through its own route. */
const SAMPLE_STATUSES: SampleStatus[] = [
  "Draft", "Sent", "Received", "Testing", "Approved", "Rejected", "Completed",
];

/** Same tone map the list page uses, so a status reads identically everywhere. */
const STATUS_TONE: Record<string, "neutral" | "info" | "warn" | "ok" | "danger"> = {
  Draft: "neutral", Sent: "info", Received: "info", Testing: "warn",
  Approved: "ok", Rejected: "danger", Completed: "ok",
};

type HeaderDraft = SampleHeaderDraft;

/** Server rows are deleted and reinserted on every save, so their ids are not
 *  stable — key the draft on a fresh uuid instead. */
const attachmentFromDto = (a: SampleAttachmentDto): AttachmentDraft => ({
  key: crypto.randomUUID(),
  type: a.type,
  fileName: a.fileName ?? "",
  fileUrl: a.fileUrl ?? "",
  fileKey: a.fileKey ?? "",
  fileSize: a.fileSize ?? 0,
  mimeType: a.mimeType ?? "",
});

/**
 * Remount on id change so a cached order's form state — including
 * uploaded-but-unsaved files — can never bleed into the next one. The route
 * element is stable across /sample-orders/:a → /sample-orders/:b, so the key
 * has to live in this wrapper.
 */
export function SampleOrderEditorPage() {
  const { id } = useParams();
  return <SampleOrderEditorForm key={id ?? "new"} />;
}

function SampleOrderEditorForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = useCan();
  const isNew = !id || id === "new";

  const { data: existing, isLoading, error: loadError, refetch } = useSampleOrder(isNew ? undefined : id);
  // Shortcut from the product list: /sample-orders/new?productId=… opens the
  // editor with that product already on line 1.
  const [searchParams] = useSearchParams();
  const contextParams=new URLSearchParams(searchParams);contextParams.delete("productId");
  const context=contextParams.size ? `?${contextParams}` : "";
  const listUrl=`/sample-orders${context}`;
  const seedProductId = isNew ? searchParams.get("productId") : null;
  const { data: seedProduct } = useProduct(seedProductId ?? undefined);
  const suppliers = useSuppliers({});
  const customers = useCustomers({});
  const settings = useSettings();
  const create = useCreateSampleOrder();
  const update = useUpdateSampleOrder(id ?? "new");
  const [catalogOpen,setCatalogOpen]=useState(false);
  const [catalogBusy,setCatalogBusy]=useState(false);
  const [printOpen,setPrintOpen]=useState(false);
  const [mailOpen,setMailOpen]=useState(false);
  const [catalogMsg, setCatalogMsg] = useState<string | null>(null);
  const del = useDeleteSampleOrder();

  const lists = settings.data?.businessOperations.optionLists;
  const productTypes = lists?.productTypes ?? [];

  // Đơn mẫu lấy từ nhà cung cấp riêng tư mình không được thấy (28/10/2026): chỉ xem — máy chủ cũng chặn (`private_supplier`).
  const supplierHidden = existing?.supplierHidden === true;
  const canEdit = user ? can(isNew ? "records:create" : "records:update") && !supplierHidden : false;
  const canDelete = user ? can("records:delete") && !supplierHidden : false;
  // "Add to DB" TẠO sản phẩm, nên đòi quyền tạo chứ không phải quyền sửa đơn.
  const canCreate = user ? can("records:create") : false;

  const [h, setH] = useState<HeaderDraft>(initialSampleHeader);
  const [lines, setLinesState] = useState<SampleLineDraft[]>([]);
  const [attachments, setAttachmentsState] = useState<AttachmentDraft[]>([]);
  const [status, setStatus] = useState<SampleStatus>("Draft");
  const [picking, setPicking] = useState(false);
  const [linesBusy, setLinesBusy] = useState(false);
  const [attachBusy, setAttachBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mốc lần lưu thành công gần nhất — nút Lưu đổi thành "Đã lưu" trong 2
  // giây. Không có tín hiệu nào thì người dùng bấm thêm lần nữa cho chắc.
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const initialized=useRef(false);
  /** Mục "Gửi hàng mẫu" mở sẵn khi đơn mở ra đã có dữ liệu gửi hàng — chốt một lần lúc nạp. */
  const shippingOpenAtLoad = useRef<boolean | null>(null);
  const uploading = linesBusy || attachBusy;
  const saving = create.isPending || update.isPending;
  const busy = uploading || saving || del.isPending || catalogBusy;
  const readOnly = !canEdit || busy;

  /** Set right before a navigation we initiate, so the blocker lets it through. */
  const allowNav = useRef(false);

  const remarksId = useId();
  const statusId = useId();

  const patchHeader = (patch: Partial<HeaderDraft>) => {
    setDirty(true);
    setH((prev) => ({ ...prev, ...patch }));
  };
  const setLines = (next: SampleLineDraft[] | ((previous:SampleLineDraft[])=>SampleLineDraft[])) => {
    setDirty(true);
    setLinesState(next);
  };
  const setAttachments = (next: AttachmentDraft[] | ((previous:AttachmentDraft[])=>AttachmentDraft[])) => {
    setDirty(true);
    setAttachmentsState(next);
  };

  useEffect(() => {
    if (!existing || initialized.current) return;
    initialized.current=true;
    const loaded: HeaderDraft = {
      orderType: existing.orderType,
      supplierId: existing.supplierId ?? "",
      customerId: existing.customerId ?? "",
      orderDate: toDateInput(existing.orderDate),
      deliveryDate: toDateInput(existing.deliveryDate),
      teamDepartment: existing.teamDepartment ?? "",
      labTestRequest: existing.labTestRequest ?? "",
      courier: existing.courier ?? "",
      trackingNo: existing.trackingNo ?? "",
      shippedAt: toDateInput(existing.shippedAt),
      printLanguage: existing.printLanguage ?? "en",
      remarks: existing.remarks ?? "",
      contactPerson: existing.contactPerson ?? "",
      contactPhone: existing.contactPhone ?? "",
      contactEmail: existing.contactEmail ?? "",
    };
    shippingOpenAtLoad.current = sampleShippingFilled(loaded) > 0;
    setH(loaded);
    setLinesState(existing.items.map(sampleLineFromDto));
    setAttachmentsState(existing.attachments.map(attachmentFromDto));
    setStatus(existing.status);
    setDirty(false);
  }, [existing]);

  /**
   * Seed line 1 from ?productId, once.
   *
   * Deliberately setLinesState and not setLines: the user has typed nothing, so
   * this must not mark the form dirty. Otherwise clicking the shortcut and
   * changing your mind raises an "unsaved changes" prompt about a line you never
   * entered — and nothing is lost anyway, the URL recreates it.
   */
  const seeded = useRef(false);
  useEffect(() => {
    if (!seedProduct || seeded.current) return;
    seeded.current = true;
    setLinesState([sampleLineFromProduct(seedProduct)]);
  }, [seedProduct]);

  /** In-app navigation guard — uploaded R2 objects are already spent. */
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    (dirty || busy) && !allowNav.current && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

  /** Reload / tab close guard. */
  useEffect(() => {
    if (!dirty && !busy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty,busy]);

  const leaveTo = (path: string) => {
    allowNav.current = true;
    setDirty(false);
    navigate(path, { replace: true });
  };

  usePageCrumbs(
    isNew
      ? [{ label: t("samples.newOrder") }]
      : existing
        ? [{ label: existing.sampleOrderNumber }]
        : [],
    listUrl,
  );

  if (!isNew && isLoading) {
    return <PageBody><LoadingState label={t("samples.loadingOrder")} /></PageBody>;
  }
  if (!isNew && loadError && !existing) {
    return (
      <PageBody>
        <ErrorState message={t("samples.errorOrders")} onRetry={() => void refetch()} />
      </PageBody>
    );
  }

  /**
   * Manual lines carry their own identity, so legacy required a code, a type and
   * a description on each — and refused two lines with the same code. Catalog
   * lines are already de-duplicated by the picker's `takenProductIds`.
   */
  const save = async () => {
    if(!canEdit || saving || catalogBusy || del.isPending)return;
    setError(null);
    // 1 — never POST with a half-uploaded attachment or line image.
    if (uploading) { setError(t("samples.uploadInProgress")); return; }
    const requiredView={orderType:h.orderType,supplierId:h.supplierId,customerId:h.customerId,deliveryDate:fromDateInput(h.deliveryDate),teamDepartment:h.teamDepartment,items:lines};
    const touched=new Set(Object.keys(requiredView));
    const missing=checkSampleRequired(requiredView,isNew?"create":"update",touched,existing);
    const requiredMessages:Record<string,string>={supplierId:"samples.supplierRequired",customerId:"sampleWorkspace.customerRequired",deliveryDate:"samples.deliveryDateRequired",teamDepartment:"samples.teamRequired",items:"samples.itemsRequired"};
    if(missing){setError(t(requiredMessages[missing]??"samples.errorSaveOrder"));return;}
    // 5 — at least one line.
    if (lines.length === 0) { setError(t("samples.itemsRequired")); return; }
    // 6 — per-line numeric parsing (reject junk, never coerce to 0).
    const conv = sampleDraftsToInput(lines);
    if ("error" in conv) {
      const key = conv.error.field;
      setError(t("common.lineError", { line: conv.error.line, field: t(key) }));
      return;
    }
    const manual = validateSampleLines(lines);
    if (manual) { setError(`${manual.line} · ${t(manual.messageKey === "samples.duplicateProductNo" ? "sampleWorkspace.duplicateIdentity" : manual.messageKey)}`); return; }
    // A line that is entirely blank is skipped by the converter — catch the case
    // where every line was blank.
    if (conv.items.length === 0) { setError(t("samples.itemsRequired")); return; }

    // PUT full-replaces items and attachments whenever the key is present, so
    // the COMPLETE arrays go on every save.
    const payload = {
      orderType: h.orderType,
      supplierId: h.orderType === "From Supplier" ? h.supplierId || null : null,
      customerId: h.orderType === "To Customer" ? h.customerId || null : null,
      orderDate: fromDateInput(h.orderDate),
      deliveryDate: fromDateInput(h.deliveryDate) ?? null,
      status,
      // Gửi CHUỖI RỖNG, không phải undefined: `undefined` bị JSON.stringify bỏ
      // khỏi payload nên máy chủ không hề biết người dùng vừa xoá trống ô. Hậu
      // quả là xoá ô rồi bấm Lưu thì nút đổi thành "Đã lưu" trong khi giá trị
      // cũ vẫn nguyên — báo thành công cho một việc không xảy ra. Máy chủ đã
      // sẵn sàng nhận "" và ghi NULL.
      teamDepartment: h.teamDepartment.trim(),
      labTestRequest: h.labTestRequest,
      courier: h.courier.trim() || undefined,
      trackingNo: h.trackingNo.trim() || undefined,
      shippedAt: fromDateInput(h.shippedAt) ?? null,
      printLanguage: h.printLanguage,
      remarks: h.remarks.trim(),
      contactPerson: h.contactPerson.trim(),
      contactPhone: h.contactPhone.trim(),
      contactEmail: h.contactEmail.trim(),
      items: conv.items,
      attachments: attachments.map((a) => ({
        type: a.type,
        fileName: a.fileName || undefined,
        fileUrl: a.fileUrl || undefined,
        fileKey: a.fileKey || undefined,
        fileSize: a.fileSize,
        mimeType: a.mimeType || undefined,
      })),
    };

    try {
      if (isNew) {
        const r = await create.mutateAsync(payload);
        leaveTo(`/sample-orders/${r.data.id}${context}`);
      } else {
        await update.mutateAsync(payload);
        setDirty(false);
        setSavedAt(Date.now());
      }
    } catch (e) {
      setError(sampleError(e,t));
    }
  };

  const onStatusChange = (next:SampleStatus) => {setStatus(next);setDirty(true);};

  const onDelete = async () => {
    if (!existing) return;
    try {
      await del.mutateAsync(existing.id);
      allowNav.current = true;
      setDirty(false);
      navigate(listUrl);
    } catch (e) {
      setError(sampleError(e,t));
    }
  };

  /**
   * Báo lại theo TỪNG NHÓM. Bản cũ chỉ hiện "đã thêm N sản phẩm", nên người
   * dùng không biết vì sao 3 dòng chỉ vào được 1.
   */
  const onCatalogCommitted = async () => {
    const fresh=await refetch();
    if(fresh.data) {setLinesState(fresh.data.items.map(sampleLineFromDto));setCatalogMsg(t("sampleWorkspace.catalogDone"));}
  };

  const selectClass = "h-9 w-full";
  const partyName = existing ? (existing.orderType === "To Customer" ? existing.customerName : existing.supplierName) : null;
  const shippingFilled = sampleShippingFilled(h);

  /*
   * Bố cục (19/09/2026, cùng chuẩn biểu mẫu sản phẩm và PI — sampleFormLayout.ts): tiêu đề + việc của chứng từ ở đầu trang
   * (không còn nút Hủy / Lưu ở đó); Đối tác; Tiến độ (gập Gửi hàng mẫu, tự mở khi có dữ liệu); Ghi chú đơn; Dòng mẫu; Tài liệu
   * đính kèm; một thanh Hủy / Lưu dính đáy.
   */
  return (
    <PageBody className="space-y-4">
      {printOpen&&existing&&<SamplePrintPreview order={existing} onClose={()=>setPrintOpen(false)}/>}
      {mailOpen&&existing&&<SendDocumentMail onClose={()=>setMailOpen(false)} doc={{
        kind:"sampleOrder", documentNo:existing.sampleOrderNumber, customerName:existing.customerName??null,
        date:existing.orderDate?vnDate(existing.orderDate):null,
        productCodes:[...new Set(existing.items.map(i=>i.productNo).filter(Boolean) as string[])],
      }}/>}
      {catalogOpen&&existing&&<SampleCatalogDialog id={existing.id} onClose={()=>setCatalogOpen(false)} onCommitted={onCatalogCommitted} onBusyChange={setCatalogBusy}/>}
      <PageHeader
        title={isNew ? t("samples.newOrder") : t("sampleWorkspace.editTitle")}
        subtitle={existing ? [existing.sampleOrderNumber, partyName].filter(Boolean).join(" · ") : t("samples.autoNumberHint")}
        actions={
          <PageActions>
            {!readOnly && <PrintLanguageToggle value={h.printLanguage} onChange={(lang)=>patchHeader({printLanguage:lang})} />}
            {!isNew && existing && <Badge tone={STATUS_TONE[existing.status] ?? "neutral"}>{translateStatus(t, existing.status)}</Badge>}
            {!isNew && existing && (
              <Button variant="secondary" size="sm" disabled={dirty||busy} onClick={()=>setPrintOpen(true)}>
                <Printer className="size-4" /> {t("sampleWorkspace.printPreview")}
              </Button>
            )}
            {!isNew && existing && (
              <Button variant="secondary" size="sm" disabled={busy} onClick={()=>setMailOpen(true)}>
                {t("mail.title")}
              </Button>
            )}
            {/* "Add to DB": đẩy dòng gõ tay lên danh mục. Chỉ hiện khi đơn THỰC SỰ có dòng gõ tay chưa nối danh mục — một nút
                không làm gì cả còn tệ hơn là không có nút. */}
            {!isNew && existing && canCreate && existing.items.some((it) => it.isManualEntry && !it.productId) && (
              <Button variant="secondary" size="sm" disabled={dirty||busy} onClick={() => setCatalogOpen(true)}>
                <PackagePlus className="size-4" /> {t("samples.addToProducts")}
              </Button>
            )}
            {!isNew && existing && canDelete && (
              <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} aria-label={t("samples.deleteOrder")}>
                <Trash2 className="size-4" />
              </Button>
            )}
          </PageActions>
        }
      />
      <ConfirmDialog
        open={confirmDelete}
        title={existing?.sampleOrderNumber ?? t("common.delete")}
        description={t("samples.confirmDeleteOrder")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void onDelete()}
      />
      {catalogMsg && (
        <p className="rounded-sm border border-line bg-sunken/40 px-3 py-2 text-xs text-ink-soft">{catalogMsg}</p>
      )}
      {user && !canEdit && (
        <p className="rounded-sm border border-line bg-sunken/40 px-3 py-2 text-xs text-ink-soft">{supplierHidden ? t("common.internalSupplierHint") : t("errors.readOnly")}</p>
      )}
      {dirty && !isNew && <p className="text-xs text-ink-soft">{t("sampleWorkspace.saveBeforeActions")}</p>}

      <fieldset disabled={readOnly} className="min-w-0 space-y-4">
        {/* Đối tác — nhà cung cấp hoặc khách (theo loại đơn) và người liên hệ chốt trên đơn. */}
        <FormGroup id="sample-group-partner" title={t("sampleWorkspace.groups.partner")} hint={t("sampleWorkspace.groups.partnerHint")}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-12">
            <div className="@md:col-span-4">
              <FieldLabel htmlFor="sample-orderType">{t("samples.orderType")}</FieldLabel>
              <Select
                containerClassName={selectClass}
                id="sample-orderType" value={h.orderType}
                onChange={(e) => patchHeader({ orderType: e.target.value as SampleOrderType, supplierId:"", customerId:"", contactPerson:"",contactPhone:"",contactEmail:"" })}
              >
                <option value="From Supplier">{t("samples.fromSupplier")}</option>
                <option value="To Customer">{t("samples.toCustomer")}</option>
              </Select>
            </div>
            {/* Chỉ 12 / 302 đơn là "Đến khách hàng"; hệ cũ cũng đổi cả khối đối tác theo loại đơn, nên ô đối tác đi theo loại. */}
            {h.orderType === "To Customer" ? (
              <div className="@md:col-span-8">
                <FieldLabel>{t("common.customer")}</FieldLabel>
                {/* Ô CÓ TÌM KIẾM, kèm địa chỉ/liên hệ ở dòng phụ: <select> phẳng thì cuộn tay qua hàng trăm dòng, và hai khách
                    trùng tên trông y hệt nhau. */}
                <SearchableFilter
                  className={selectClass}
                  label={t("common.customer")}
                  allLabel={t("samples.selectCustomer")}
                  searchPlaceholder={t("samples.selectCustomer")}
                  value={h.customerId}
                  isLoading={customers.isLoading}
                  error={customers.error ? t("customers.errorLoad") : null}
                  onRetry={() => void customers.refetch()}
                  options={(customers.data?.data ?? []).map((c) => ({
                    value: c.id,
                    label: c.companyName,
                    sublabel: [c.address, c.phone].filter(Boolean).join(" · ") || null,
                  }))}
                  onChange={(v) =>
                    // Đổi khách thì XOÁ liên hệ đã chốt: người đó thuộc về khách cũ. Không xoá thì đơn in ra mang tên và số
                    // điện thoại của công ty khác. Danh sách khách chỉ trả `contactPreview` (chuỗi), không đủ để tự điền lại.
                    patchHeader({ customerId: v, contactPerson: "", contactPhone: "", contactEmail: "" })
                  }
                />
              </div>
            ) : (
              <div className="@md:col-span-8">
                <FieldLabel>{t("common.supplier")}</FieldLabel>
                <SearchableFilter
                  className={selectClass}
                  label={t("common.supplier")}
                  allLabel={t("samples.selectSupplier")}
                  searchPlaceholder={t("samples.selectSupplier")}
                  value={h.supplierId}
                  isLoading={suppliers.isLoading}
                  error={suppliers.error ? t("suppliers.errorLoad") : null}
                  onRetry={() => void suppliers.refetch()}
                  options={(suppliers.data?.data ?? []).map((sp) => ({
                    value: sp.id,
                    label: sp.companyName,
                    sublabel: [sp.address, sp.phone].filter(Boolean).join(" · ") || null,
                  }))}
                  onChange={(v) => {
                    // Đổi nhà cung cấp thì tự điền lại người liên hệ từ liên hệ ĐẦU TIÊN của họ — đúng như hệ cũ, và đúng như
                    // máy chủ vẫn làm khi tạo đơn (sampleOrders.ts snapshotContact). Không thấy thì dọn trắng.
                    const sp = (suppliers.data?.data ?? []).find((x) => x.id === v);
                    const c = sp?.contacts?.[0];
                    patchHeader({ supplierId: v, contactPerson: c?.name ?? "", contactPhone: c?.phone1 ?? "", contactEmail: c?.email ?? "" });
                  }}
                />
              </div>
            )}
            {/* Người liên hệ được CHỐT lên đơn khi tạo (migration 0011). Sửa được: đối tác có thể cử người khác phụ trách riêng đơn
                mẫu này, và bản in phải ghi đúng người đó. */}
            <div className="@md:col-span-4">
              <FieldLabel htmlFor="sample-contactPerson">{t("samples.contactPerson")}</FieldLabel>
              <Input id="sample-contactPerson" value={h.contactPerson} onChange={(e) => patchHeader({ contactPerson: e.target.value })} aria-describedby="sample-contactPerson-hint" />
              <Hint id="sample-contactPerson-hint">{t("samples.contactHint")}</Hint>
            </div>
            <div className="@md:col-span-4">
              <FieldLabel htmlFor="sample-contactPhone">{t("samples.contactPhone")}</FieldLabel>
              <Input id="sample-contactPhone" className="font-mono" value={h.contactPhone} onChange={(e) => patchHeader({ contactPhone: e.target.value })} />
            </div>
            <div className="@md:col-span-4">
              <FieldLabel htmlFor="sample-contactEmail">{t("samples.contactEmail")}</FieldLabel>
              <Input type="email" id="sample-contactEmail" value={h.contactEmail} onChange={(e) => patchHeader({ contactEmail: e.target.value })} />
            </div>
          </div>
        </FormGroup>

        {/* Tiến độ — ngày, bộ phận, trạng thái; gửi hàng mẫu gập lại (hãng vận chuyển / số vận đơn 0% đơn có). */}
        <FormGroup id="sample-group-schedule" title={t("sampleWorkspace.groups.schedule")} hint={t(isNew ? "sampleWorkspace.groups.scheduleHintNew" : "sampleWorkspace.groups.scheduleHint")}>
          <div className={cn("grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-2", isNew ? "@3xl:grid-cols-3" : "@3xl:grid-cols-4")}>
            <div>
              <FieldLabel htmlFor="sample-orderDate">{t("samples.orderDate")}</FieldLabel>
              <Input type="date" id="sample-orderDate" value={h.orderDate} onChange={(e) => patchHeader({ orderDate: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="sample-deliveryDate">{t("samples.deliveryDate")}</FieldLabel>
              <Input type="date" id="sample-deliveryDate" value={h.deliveryDate} onChange={(e) => patchHeader({ deliveryDate: e.target.value })} />
              {isNew || existing?.deliveryDate ? <Hint>{t("sampleWorkspace.required")}</Hint> : null}
            </div>
            <div>
              <FieldLabel htmlFor="sample-teamDepartment">{t("samples.team")}</FieldLabel>
              <Input id="sample-teamDepartment" value={h.teamDepartment} onChange={(e) => patchHeader({ teamDepartment: e.target.value })} />
              {isNew || existing?.teamDepartment ? <Hint>{t("sampleWorkspace.required")}</Hint> : null}
            </div>
            {!isNew && (
              <div>
                <FieldLabel htmlFor={statusId}>{t("common.status")}</FieldLabel>
                <Select id={statusId} containerClassName={selectClass} value={status} disabled={readOnly}
                  onChange={(e) => void onStatusChange(e.target.value as SampleStatus)}>
                  {SAMPLE_STATUSES.map((s) => <option key={s} value={s}>{translateStatus(t, s)}</option>)}
                </Select>
              </div>
            )}
          </div>
          {/*
            Ô "Lab test request" đã ẨN theo yêu cầu khách 13/09/2026: họ không nhập nữa, và bản in cũng bỏ dòng đó. Cột
            `lab_test_request` và dữ liệu cũ GIỮ NGUYÊN (sampleFormLayout.ts: SAMPLE_HEADER_HIDDEN_BY_DECISION).

            Gửi hàng mẫu tách ba trường thay vì một ô chữ tự do: số vận đơn là thứ người ta tra suốt ("hàng mẫu tới đâu rồi").
          */}
          <MoreBlock
            title={t("sampleWorkspace.groups.shipping")}
            defaultOpen={shippingOpenAtLoad.current ?? false}
            filledLabel={shippingFilled > 0 ? t("sampleWorkspace.filled", { count: shippingFilled }) : null}
          >
            <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-3">
              <div>
                <FieldLabel htmlFor="sample-courier">{t("samples.courier")}</FieldLabel>
                <Input id="sample-courier" value={h.courier} onChange={(e) => patchHeader({ courier: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="sample-trackingNo">{t("samples.trackingNo")}</FieldLabel>
                <Input id="sample-trackingNo" className="font-mono" value={h.trackingNo} onChange={(e) => patchHeader({ trackingNo: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="sample-shippedAt">{t("samples.shippedAt")}</FieldLabel>
                <Input type="date" id="sample-shippedAt" value={h.shippedAt} onChange={(e) => patchHeader({ shippedAt: e.target.value })} />
              </div>
            </div>
          </MoreBlock>
        </FormGroup>

        <FormGroup id="sample-group-notes" title={t("samples.orderRemarks")}>
          <textarea
            id={remarksId}
            aria-label={t("samples.orderRemarks")}
            rows={2}
            value={h.remarks}
            onChange={(e) => patchHeader({ remarks: e.target.value })}
            className="w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none transition-colors focus:border-brand"
          />
        </FormGroup>
      </fieldset>

      <SampleOrderLinesEditor
        lang={h.printLanguage}
        lines={lines}
        readOnly={readOnly}
        onChange={setLines}
        productTypes={productTypes}
        onAddProduct={() => setPicking(true)}
        onUploadStateChange={setLinesBusy}
      />

      <SampleOrderAttachments
        readOnly={readOnly}
        attachments={attachments}
        onChange={setAttachments}
        onBusyChange={setAttachBusy}
      />

      {error && <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger" role="alert">{error}</p>}

      {picking ? (
        <ProductPickerModal
          takenProductIds={lines.map((l) => l.productId).filter(Boolean)}
          onClose={() => setPicking(false)}
          onPick={(p) => { setLines([...lines, sampleLineFromProduct(p)]); setPicking(false); }}
        />
      ) : null}

      <ConfirmDialog open={blocker.state==="blocked"} title={t("common.unsavedTitle")} description={t("samples.unsavedUploadsDescription")}
        confirmLabel={t("common.discardChanges")} cancelLabel={t("common.keepEditing")} busy={busy}
        onCancel={()=>blocker.reset?.()} onConfirm={()=>blocker.proceed?.()}/>
      {/* Một thanh Hủy / Lưu duy nhất, dính đáy (19/09/2026 — bỏ cặp nút Hủy / Lưu thứ hai ở đầu trang). */}
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
        <p className="mr-auto text-xs text-ink-faint">{t("sampleWorkspace.saveHint")}</p>
        <Button variant="secondary" disabled={busy} onClick={()=>navigate(listUrl)}>{t("common.cancel")}</Button>
        {canEdit&&<SaveButton onClick={()=>void save()} saving={saving} savedAt={savedAt} disabled={busy}/>}
      </div>
    </PageBody>
  );
}
